import { ApolloLink, HttpLink, Operation } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import AuthManager from 'components/auth/AuthManager';
import { featureCountryCode } from 'states/persistInStorage';
import { useAppConfigStore } from 'stores/appConfigStore';
import { useAuthStore } from 'stores/authStore';
import { Query } from 'types/generatedSchemaTypes';
import config from '../config/config';
import Logger from './Logger';

const RETRY_COUNT = 3;

const INCREASED_RETRY_OPERATIONS: (keyof Query)[] = [
  'readCustomerV3',
  'readParkingSessions',
  'readOngoingChargingSessionV3',
];

const SKIP_RETRY_OPERATIONS: (keyof Query)[] = [
  'parkZonesByBoundingBox',
  'readParkZonePriceEstimate',
];

const skipRetry = (operation: Operation, error: Error | undefined) => {
  const skipOperation = SKIP_RETRY_OPERATIONS.includes(
    operation.operationName as keyof Query
  );
  const skipStatus = ['400', '401', '404'].some((status) =>
    error?.message.includes(status)
  );
  return skipOperation || skipStatus;
};

const getMaxRetry = (operation: Operation) => {
  return INCREASED_RETRY_OPERATIONS.includes(
    operation.operationName as keyof Query
  )
    ? RETRY_COUNT + 2
    : RETRY_COUNT;
};

export const createRetryLink = (): RetryLink => {
  return new RetryLink({
    delay: {
      jitter: true,
      initial: 1000,
      max: Infinity,
    },
    attempts: (count, operation, error: Error | undefined) => {
      return (
        count < getMaxRetry(operation) &&
        !!error &&
        !skipRetry(operation, error)
      );
    },
  });
};

export const createErrorLink = () => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    graphQLErrors?.forEach(async (err) => {
      switch (err?.extensions?.code) {
        // Apollo Server sets code to UNAUTHENTICATED/401
        // when an AuthenticationError is thrown in a resolver
        case 401:
        case 'UNAUTHENTICATED':
          // Modify the operation context with a new token
          operation.setContext({
            headers: {
              ...operation.getContext().headers,
              'X-Auth-Token': await AuthManager.getAccessTokenAsync(),
            },
          });
          // Retry the request, returning the new observable
          return forward(operation);
      }
    });
    if (networkError && networkError.name !== 'AbortError') {
      Logger.warn(
        `Network error | Error: ${networkError.message}`,
        JSON.stringify({
          networkError: networkError,
        })
      );
    }
  });
};

export const createAuthLink = () => {
  return setContext(async (_, { headers }) => {
    const baseHeaders = {
      ...headers,
      'apollo-require-preflight': 'true',
    };

    const bypassAuth = config.useMockedAuthentication;
    if (bypassAuth === 'true' && process.env.NODE_ENV === 'development') {
      useAuthStore.getState().setTokens({
        accessToken: 'accessToken',
        refreshToken: 'refreshToken',
        expireTime: 'accessTokenExpirationDate',
        loginLoading: false,
      });

      return {
        headers: {
          ...baseHeaders,
          'X-bypass-auth': bypassAuth,
          'X-customer-uid': config.mockCustomerUid,
          'X-azure-id': config.mockAzureUid,
        },
      };
    }

    const currentToken = await AuthManager.getAccessTokenAsync();
    const appCountry = featureCountryCode();
    return {
      headers: {
        ...baseHeaders,
        ...(currentToken && { 'X-Auth-Token': currentToken }),
        ...(appCountry && { 'X-Country-Code': appCountry }),
      },
    };
  });
};

export const createHttpLink = () => {
  // this will be used in non-development environment - ensuring no leakage
  const httpLink = new HttpLink({
    uri: config.graphqlUrl,
  });

  // this will only for development
  const dynamicHttpLink = new ApolloLink((operation, forward) => {
    return new HttpLink({
      uri: useAppConfigStore.getState().bffUrl,
    }).request(operation, forward);
  });

  return process.env.NODE_ENV === 'development' ? dynamicHttpLink : httpLink;
};
